import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Category, type GameV1 } from '@/api/games';
import { GamesGrid, GamesGridStatic } from '@/components/GamesGrid';
import { SectionTitle } from '@/components/SectionTitle';
import { useTopGamePreview } from '@/hooks/useTopGamePreview';
import type { TSearchResultGameOpenType } from '@/modules/googleAnalytics/events/searchGamesEvents';
import { useCMSGameCategories } from '@/modules/lobby/hooks';
import { useGetCommonStore } from '@/store';
import { ScResult } from '../../styled';
import type { ISearchGridOptions } from '../ResultWithSimilar';

interface ISearchGameCategoriesProps {
  gridOptions: ISearchGridOptions;
  handleSearchGameClick: (
    game: GameV1,
    type: TSearchResultGameOpenType
  ) => void;
}

export const SearchGameCategories: FC<ISearchGameCategoriesProps> = ({
  gridOptions,
  handleSearchGameClick,
}) => {
  const { t } = useTranslation('common');

  const { categories: gameCategoriesList } = useCMSGameCategories();
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated, favoriteGames, recentGames } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      favoriteGames: state.favoriteGames,
      recentGames: state.recentGames,
    }))
  );

  const topGames = useTopGamePreview(gameCategoriesList.length > 0);

  const recentGamesList = useMemo(
    () => (recentGames.length && isAuthenticated ? recentGames : [topGames[0]]),
    [isAuthenticated, recentGames, topGames]
  );

  const isInitialLoading = !gameCategoriesList.length;

  return isAuthenticated ? (
    <>
      <ScResult>
        <SectionTitle>{t('game.recentlyPlayed')}</SectionTitle>
        <GamesGridStatic
          {...gridOptions}
          games={recentGamesList}
          isSearchModal
          loading={isInitialLoading}
          onDemoClick={(game) => handleSearchGameClick(game, 'recently_played')}
          onPlayClick={(game) => handleSearchGameClick(game, 'recently_played')}
        />
      </ScResult>
      <ScResult>
        <SectionTitle>{t('game.favorites')}</SectionTitle>
        <GamesGridStatic
          {...gridOptions}
          games={favoriteGames}
          isSearchModal
          loading={isInitialLoading}
          onDemoClick={(game) => handleSearchGameClick(game, 'favorites')}
          onPlayClick={(game) => handleSearchGameClick(game, 'favorites')}
        />
        {!(isInitialLoading || favoriteGames.length) && (
          <p className="empty-list">{t('game.emptyFavorites')}</p>
        )}
      </ScResult>
    </>
  ) : (
    <>
      <ScResult>
        <SectionTitle>{t('game.topGames')}</SectionTitle>
        <GamesGrid
          category={Category.TOP}
          {...gridOptions}
          isSearchModal
          onDemoClick={(game) => handleSearchGameClick(game, 'top')}
          onPlayClick={(game) => handleSearchGameClick(game, 'top')}
        />
      </ScResult>
      <ScResult>
        <SectionTitle>{t('gameCategories.trending')}</SectionTitle>
        <GamesGrid
          category={Category.TRENDING}
          {...gridOptions}
          isSearchModal
          onDemoClick={(game) => handleSearchGameClick(game, 'trending')}
          onPlayClick={(game) => handleSearchGameClick(game, 'trending')}
        />
      </ScResult>
    </>
  );
};
